import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { CreatableMissionFile } from "@nova/schemas"
import { useSWRNoRevalidate } from "@nova/swr"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppSelector } from "integrations/redux/hooks"
import useSWRMutation from "swr/mutation"

const getMissionFiles = (orgId: string, missionId: string) => {
    return fetchApiGateway("get /org/:orgId/fleet/mission/:missionId/file", {
        params: { orgId, missionId },
    })
}

export const useMissionFiles = (missionId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading, mutate } = useSWRNoRevalidate(
        orgId ? ["get-mission-files", orgId, missionId] : null,
        () => (orgId ? getMissionFiles(orgId, missionId) : undefined)
    )

    return { data, isLoading, mutate }
}

const createMissionFile = (orgId: string, missionId: string, body: CreatableMissionFile) => {
    return fetchApiGateway("post /org/:orgId/fleet/mission/:missionId/file", {
        body,
        params: { orgId, missionId },
    })
}

export const useCreateMissionFile = (missionId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const { mutate } = useMissionFiles(missionId)

    const { trigger, isMutating } = useSWRMutation(
        ["create-mission-file", orgId, missionId],
        (_key, { arg }: { arg: CreatableMissionFile }) =>
            orgId ? createMissionFile(orgId, missionId, arg) : undefined,
        { onSuccess: () => mutate() }
    )

    return {
        createMissionFile: trigger,
        isCreating: isMutating,
    }
}

const deleteMissionFile = (orgId: string, missionId: string, fileId: string) => {
    return fetchApiGateway("delete /org/:orgId/fleet/mission/:missionId/file/:fileId", {
        params: { orgId, missionId, fileId },
    })
}

export const useDeleteMissionFile = (missionId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const { mutate } = useMissionFiles(missionId)

    const { trigger, isMutating } = useSWRMutation(
        ["delete-mission-file", orgId, missionId],
        (_key, { arg }: { arg: string }) =>
            orgId ? deleteMissionFile(orgId, missionId, arg) : undefined,
        { onSuccess: () => mutate() }
    )

    return {
        deleteMissionFile: trigger,
        isDeleting: isMutating,
    }
}

const getMissionChecklistSubmissions = (orgId: string, missionId: string) => {
    return fetchApiGateway("get /org/:orgId/fleet/mission/:missionId/checklist-submissions", {
        params: { orgId, missionId },
    })
}

export const useMissionChecklistSubmissions = (missionId: string) => {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading } = useSWRNoRevalidate(
        orgId ? ["get-mission-checklist-submissions", orgId, missionId] : null,
        () => (orgId ? getMissionChecklistSubmissions(orgId, missionId) : undefined)
    )

    return { data, isLoading }
}

export const missionFileS3Key = (orgId: string, missionId: string, fileId: string) => {
    return `public/org/${orgId}/pilot-tracking/mission/${missionId}/files/${fileId}`
}
